import {omit, startCase} from 'lodash';
import type {Environment} from 'react-relay';
import {
	type ConcreteRequest,
	type Disposable,
	type GraphQLTaggedNode,
	type MutationConfig,
	type MutationParameters,
	commitMutation,
} from 'relay-runtime';
import {type EventProperties, type TrackingOutput, logTrackingError, trackMutation} from './TrackingV2';

/**
 * Fields that should always be excluded entirely from tracking
 */
const excludedFields = [
	'aboveTaskOrder',
	'aboveFavouredTaskOrder',
	'companyId',
	'companyProjectId',
	'csrfToken',
	'deleteNode',
	'existingAssignedPersons',
	'id',
	'idleTimeId',
	'ids',
	'optimisticColumnId',
	'optimisticSprintId',
	'optimisticTaskOrder',
	'personIds',
	'previousAssignedPersons',
	'prevStatusColumnId',
	'prevProjectGroupStatusColumnId',
	'prevStatusColumnIdMap',
	'projectId',
	'taskId',
	'socketClientId',
	'timeLeftOptimistic',
	'viewerId',
	'viewerActualId',
	'companyId',
	'adminPassword',
	'newPassword',
	'oldPassword',
	'password',
	'publicCreateTaskPassword',
];

/**
 * Clean the input by omitting the excluded fields and ignored changes
 */
const cleanInput = (input: Record<string, unknown>, ignoredChanges: string[] = []): Record<string, unknown> => {
	const ignoredFields = excludedFields.concat(ignoredChanges);

	return omit(input, ignoredFields);
};

/**
 * Fields that should be excluded from list of changed fields
 */
const excludedChangedFields = ['mutationAffectedCount'];

/**
 * Convert input key to more easy to understand string
 */
const mapKeyToChangedField = (key: string): string => {
	switch (key) {
		case 'aboveTaskId':
			return 'sortOrder';
		default:
			return key;
	}
};

const getChangesFromMutationInput = (cleanInput: Record<string, unknown>): string[] => {
	return Object.entries(cleanInput)
		.filter(([key, value]) => !excludedChangedFields.includes(key) && value !== undefined)
		.map(([key]) => mapKeyToChangedField(key));
};

// Mutation name can sometimes be nested under default, which does not exist on the exported MutationConfig type - not sure why
const typeIsConcreteRequest = (mutation: GraphQLTaggedNode): mutation is ConcreteRequest & {default?: ConcreteRequest} => {
	if (mutation) {
		return true;
	}

	return false;
};

/**
 * Trims the " Mutation" ending from mutation names and prepends instead, and converts to Formal Case
 */
const formatMutationName = (mutationName: string): string => {
	return startCase(mutationName.replace('Mutation', '').replace('ModernMutation', ''));
};

/**
 * @param mutationName object being mutated.
 * @param input the variables supplied to the mutation.
 * @param changesInTrackName set true to include list of changes in tracking name, should primarily be used for mutations that do a bunch of different things
 * @param ignoredChanges list of keys that will be ignored from change list
 * @param additionalTracking Key-value pairs to include in mutation tracking without needing it to be included in mutation input
 * @returns an object containing properties that were tracked to facilitate testing.
 */
const mutationTrack = (
	mutationName: string,
	input: Record<string, unknown>,
	changesInTrackName: boolean,
	ignoredChanges: string[],
	additionalTracking: Record<string, unknown>
): TrackingOutput<EventProperties> | Error => {
	if (!mutationName) throw new Error('TrackingError: mutationTrack must be called with mutationName');
	if (!input) throw new Error('TrackingError: mutationTrack must be called with input');

	const extendedInput = {...input, ...additionalTracking};
	const cleanedInput = cleanInput(extendedInput, ignoredChanges);
	const changedFields = getChangesFromMutationInput(cleanedInput);
	const formattedMutationName = formatMutationName(mutationName);

	const changeString = changedFields.map(change => startCase(change)).join(', ');
	const mutationString = `Mutation: ${formattedMutationName} ${changesInTrackName ? `- Changed: ${changeString}` : ''}`;

	const mutationProperties = {
		changedFields,
		input: cleanedInput,
		trackingType: 'Mutation',
		mutationName: formattedMutationName,
	};

	return trackMutation(mutationString, mutationProperties);
};

type TrackCommitConfig = MutationConfig<MutationParameters & {variables?: {input: Record<string, unknown>}}>;

/**
 * @param config the config to commit the mutation with
 * @param changesInTrackName set true to include list of changes in tracking name, should primarily be used for mutations that do a bunch of different things
 * @param ignoredChanges list of keys that will be ignored from change list
 * @param additionalTracking Key-value pairs to include in mutation tracking without needing it to be included in mutation input
 * @returns an object containing properties that were tracked to facilitate testing.
 * Optionally contains error if function threw during execution. If input or mutationName is missing from config, returns nothing and doesn't track.
 */
const trackCommit = (
	config: TrackCommitConfig,
	changesInTrackName: boolean,
	ignoredChanges: string[],
	additionalTracking: Record<string, unknown>
): TrackingOutput<EventProperties> | Error | undefined => {
	try {
		const input = config.variables?.input;
		const mutation = config.mutation;
		if (typeIsConcreteRequest(mutation)) {
			const mutationName = mutation.default ? mutation.default.fragment?.name : mutation.fragment?.name;
			return mutationTrack(mutationName, input, changesInTrackName, ignoredChanges, additionalTracking);
		}
	} catch (e) {
		logTrackingError(e);
		return e;
	}
};

/**
 * Higher-level helper function to execute a mutation against a specific environment while also sending tracking info about the mutation to Amplitude.
 * Use this function instead of the standard Relay commitMutation if your mutation needs tracking.
 * Allowed params are the same as commitMutation, and additionally a set of properties to be included in tracking.
 * @param environment the environment to commit the mutation to
 * @param config the config to commit the mutation with
 * @param changesInTrackName set true to include list of changes in tracking name, should primarily be used for mutations that do a bunch of different things
 * @param ignoredChanges list of keys that will be ignored from change list
 * @param additionalTracking Key-value pairs to include in mutation tracking without needing it to be included in mutation input
 * @returns the disposable to commit the mutation
 */
export const trackAndCommitMutation = (
	environment: Environment,
	config: TrackCommitConfig,
	changesInTrackName = false,
	ignoredChanges: string[] = [],
	additionalTracking: Record<string, unknown> = {}
): Disposable => {
	trackCommit(config, changesInTrackName, ignoredChanges, additionalTracking);

	return commitMutation(environment, config);
};

/**
 * Function allowing granular control over the mutation name rather than inferring from input to react-relay commitMutation function.
 * This function should generally only be used for mutations sent outside of react-relay or to override the given name of your mutation.
 * @param mutationName object being mutated.
 * @param input the variables supplied to the mutation. If an input is not supplied, a standard tracking event is sent with the mutationObject and mutationAction. If the mutation cleans its variables, supply the cleanedVariable object.
 * @param changesInTrackName set true to include list of changes in tracking name, should primarily be used for mutations that do a bunch of different things
 * @param ignoredChanges list of keys that will be ignored from change list
 * @param additionalTracking Key-value pairs to include in mutation tracking without needing it to be included in mutation input
 * @returns an object containing properties that were tracked to facilitate testing.
 * Optionally contains error if function threw during execution. If input or mutationName is missing from config, returns nothing and doesn't track.
 */
export const trackCustomNameMutation = (
	mutationName: string,
	input: Record<string, unknown>,
	changesInTrackName = false,
	ignoredChanges: string[] = [],
	additionalTracking: Record<string, unknown> = {}
): TrackingOutput<EventProperties> | Error | undefined => {
	try {
		return mutationTrack(mutationName, input, changesInTrackName, ignoredChanges, additionalTracking);
	} catch (e) {
		logTrackingError(e);
		return e;
	}
};
